import _ from 'lodash';
import React, {
  useEffect, useState, useCallback, useMemo,
} from 'react';
import { useQuery, useMutation } from '@apollo/client';

import PropTypes from 'prop-types';
import arrayMove from 'array-move';
import { v4 } from 'uuid';

import {
  FieldArray,
  withFormik,
} from 'formik';

import { Form } from 'semantic-ui-react';
import {
  captureException,
  CenteredLoader,
  ErrorHandler,
} from '@playbookux/playbook-web-core';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { toast } from 'react-semantic-toasts';
import HandleQuestions from './components/handle-questions';

import { SortableList } from '../../../components/sortable-component';
import ConfirmationModal from './components/confirmation-modal';
import CancelModal from './components/cancel-modal';
import { SEGMENT, UPDATE_SCREENER_QUESTIONS } from './graphql';
import { questionsSchema } from './schema';

import {
  AddQuestion,
  Header,
  SectionContainer,
  Text,
  CircleItem,
  Icon as CircleIcon,
  Container,
  ButtonContainer,
  Button,
} from './styles';

import './toast.css';

import { TOAST_TIME, MODAL_MESSAGES } from './constants';

const ProjectEditScreener = (props) => {
  const [modalType, setModalType] = useState('');

  const {
    isValid,
    values,
    setValues,
    setFieldValue,
    setFieldTouched,
    formErrors,
    formPrefix,
    history,
  } = props;

  const {
    questions,
    surveyId,
  } = values;

  const { projectId, segmentId } = props.match.params;

  const [updateScreenerQuestions, { loading: updateLoading }] = useMutation(UPDATE_SCREENER_QUESTIONS);

  const typeQualify = (disqualify, qualify) => {
    if (disqualify) { return 'Disqualify'; }
    if (qualify) { return 'Qualify'; }
    return 'May Select';
  };

  const {
    data: segmentData = [],
    loading,
    error,
  } = useQuery(SEGMENT, {
    variables: {
      segmentId,
    },
  });

  const questionsQuery = segmentData?.segment?.surveys[0]?.questions;
  const querySurveyId = segmentData?.segment?.surveys[0]?.id;
  const questionsArray = useMemo(() => questionsQuery?.map((question) => {
    const { questionOptions } = question;

    const answers = _.map(questionOptions, (option) => {
      const type = typeQualify(option.disqualify, option.qualify);
      return ({
        id: v4(),
        answer: option.label,
        type,
        optionRatingPercentage: option.optionRatingPercentage,
      });
    });

    return {
      id: question.id,
      question: question.label,
      index: question.questionOrder,
      type: question.questionType,
      screenerRatingPercentage: question.screenerRatingPercentage,
      randomize: question.randomize,
      answers,
    };
  }), [questionsQuery]);

  useEffect(() => {
    setValues({
      questions: questionsArray,
      surveyId: querySurveyId,
    });
  }, [questionsArray, querySurveyId]);

  const handleOpenModal = (type) => {
    setModalType(type);
  };

  const handleCloseModal = () => {
    setModalType('');
  };

  const handleConfirmModal = async () => {
    const transformedData = {
      surveyType: 'screener',
      questionsAttributes: questions.map((question) => {
        const {
          type: questionType, question: label, index: questionOrder, answers, randomize,
        } = question;

        // Map each answer to the desired format
        const questionOptionsAttributes = answers.map((answer, order) => ({
          label: answer.answer,
          order,
          disqualify: answer.type === 'Disqualify',
          qualify: answer.type === 'Qualify',
        }));

        // Format the question object
        return {
          questionType,
          label,
          questionOrder,
          questionOptionsAttributes,
          groupId: null,
          rightLabel: null,
          leftLabel: null,
          randomize,
          surveyId,
        };
      }),
    };

    try {
      await updateScreenerQuestions({
        variables: {
          segmentId,
          attributes: transformedData,
        },
      });

      history.push(`/projects/${projectId}/study`, { shouldRefetch: true });

      toast({
        description: 'Screener updated successfully!',
        time: TOAST_TIME.DEFAULT,
        type: 'success',
        icon: <></>, // This toast does not have an option to not use an icon, so we pass an empty fragment
      });
    } catch (err) {
      captureException('UPDATE_SCREENER_QUESTIONS_FAILURE', err);
      toast({
        description: err.message || 'Screener failed to update, please try again!',
        time: TOAST_TIME.DEFAULT,
        type: 'error',
      });
    }

    setModalType('');
  };

  const handleCancelModal = async () => {
    history.push(`/projects/${projectId}/study`, {});
    setModalType('');
  };

  // if formPrefix is an empty string then this component handles template creation,
  // and does not need to send question id to API.
  const isTemplateCreation = formPrefix === '';

  const handleSortEndOptions = useCallback(({ oldIndex, newIndex }) => {
    let updatedQuestions = _.cloneDeep(questions);

    if (updatedQuestions[oldIndex]) {
      updatedQuestions = arrayMove(updatedQuestions, oldIndex, newIndex);

      // we also need to update the index of each question after sorting
      updatedQuestions = updatedQuestions.map((question, index) => ({
        ...question,
        index,
      }));

      setFieldValue('questions', updatedQuestions);
      setValues({
        ...values,
        questions: updatedQuestions,
      });
    }
  }, [questions, setValues, values]);

  const handleNewQuestion = useCallback((
    type,
    question,
    answers,
  ) => {
    let maxValue = 0;

    if (!_.isEmpty(questions)) {
      maxValue = _.max(questions?.map((t) => t.index));
    }

    const updatedQuestions = [...questions];
    updatedQuestions.push({
      id: isTemplateCreation ? undefined : v4(),
      type,
      question,
      answers,
      index: maxValue + 1,
    });

    setValues({ ...values, questions: updatedQuestions });
  }, [isTemplateCreation, questions, setValues, values]);

  const handleQuestions = HandleQuestions(questions, formErrors, formPrefix, setFieldValue, setFieldTouched);

  if (error) {
    return (
      <ErrorHandler
        error={error}
        defaultMessage="Error something went wrong!"
      />
    );
  }

  return (
    <Container>
      <SectionContainer>
        <Header>
          Edit Screener Questions
        </Header>

        <Text>
          Only unfilled sessions will receive the updated screener questions.
        </Text>

        {loading
          ? <CenteredLoader />
          : (
            <>
              <FieldArray
                name={`${formPrefix}questions`}
                render={() => {
                  const typeSingle = 'single_select';
                  const question = '';

                  const answers = [
                    {
                      id: isTemplateCreation ? undefined : v4(), answer: '', type: 'Qualify', other: false,
                    },
                    {
                      id: isTemplateCreation ? undefined : v4(), answer: '', type: 'Qualify', other: false,
                    },
                  ];

                  return (
                    <Form>
                      {questions && (
                        <SortableList
                          items={handleQuestions}
                          onSortEnd={handleSortEndOptions}
                          useDragHandle
                        />
                      )}

                      <AddQuestion
                        onClick={() => handleNewQuestion(
                          typeSingle,
                          question,
                          answers,
                        )}
                      >
                        <CircleItem
                          small
                        >
                          <CircleIcon name="plus" small />
                        </CircleItem>
                        <Text.Bold>
                          Add screener question
                        </Text.Bold>
                      </AddQuestion>
                    </Form>
                  );
                }}
              />

              <ButtonContainer>
                <Button
                  type="button"
                  onClick={() => handleOpenModal('CANCEL')}
                  backgroundColor="#FFFFFF"
                  color="#4D5473"
                >
                  Cancel
                </Button>

                <Button
                  disabled={!isValid}
                  type="button"
                  onClick={() => handleOpenModal('SAVE')}
                >
                  Save
                </Button>
              </ButtonContainer>
            </>
          )}
      </SectionContainer>

      <ConfirmationModal
        showModal={modalType === 'SAVE'}
        handleActionModal={handleConfirmModal}
        handleCloseModal={handleCloseModal}
        message={MODAL_MESSAGES[modalType]}
        loading={updateLoading}
      />

      <CancelModal
        showModal={modalType === 'CANCEL'}
        handleActionModal={handleCancelModal}
        handleCloseModal={handleCloseModal}
        message={MODAL_MESSAGES[modalType]}
      />
    </Container>
  );
};

const answersType = PropTypes.shape({
  answer: PropTypes.string,
});

const questionsType = PropTypes.shape({
  question: PropTypes.string,
  answers: PropTypes.arrayOf(answersType),
});

ProjectEditScreener.propTypes = {
  values: PropTypes.shape().isRequired,
  setValues: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  formErrors: PropTypes.shape({
    device: PropTypes.string,
    operatingSystem: PropTypes.string,
    questions: PropTypes.arrayOf(questionsType),
  }),
  formPrefix: PropTypes.string,
  history: PropTypes.shape({
    goBack: PropTypes.func,
    push: PropTypes.func,
  }).isRequired,
  isValid: PropTypes.bool.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      projectId: PropTypes.string,
      segmentId: PropTypes.string,
    }),
  }).isRequired,
};

ProjectEditScreener.defaultProps = {
  formErrors: {},
  formPrefix: '',
};

const mapStateToProps = () => ({
});

export default compose(
  connect(mapStateToProps),
  withFormik({
    validationSchema: questionsSchema,
    enableReinitialize: true,
    mapPropsToValues: () => ({
      questions: [],
      surveyId: '',
    }),
  }),
)(ProjectEditScreener);
